import { useEffect, useState } from "react";
import { passwordStrength } from "check-password-strength";

const strengthLabels = ["Too Weak", "Weak", "Medium", "Strong"];
const barColors = [
  "bg-red-500",
  "bg-orange-500",
  "bg-yellow-500",
  "bg-green-600",
];

const COMMON_PATTERNS = [
  "password",
  "123456",
  "qwerty",
  "letmein",
  "admin",
  "welcome",
  "login",
  "abc123",
];

function adjustScore(score: number, password: string): number {
  const lower = password.toLowerCase();
  const hasCommonPattern = COMMON_PATTERNS.some((pattern) =>
    lower.includes(pattern)
  );
  const hasRepeat = /(.)\1{2,}/.test(password); // aaa, 111, etc.

  if (hasCommonPattern || hasRepeat) {
    return Math.max(0, score - 1); // reduce one level, but not below 0
  }

  return score;
}

export default function PwIndicator({ password }: { password: string }) {
  const [score, setScore] = useState(0);
  const [, setFeedback] = useState("");

  useEffect(() => {
    if (password) {
      const result = passwordStrength(password);
      const adjusted = adjustScore(result.id, password);
      setScore(adjusted);
      setFeedback(result.value);
    }
  }, [password]);

  return (
    <>
      {password && (
        <div className="space-y-1 mt-1 max-w-full">
          <div className="w-full h-2 rounded bg-gray-200">
            <div
              className={`h-full rounded transition-all duration-300 ${
                score === 0
                  ? "w-1/5"
                  : score === 1
                    ? "w-2/5"
                    : score === 2
                      ? "w-3/5"
                      : "w-full"
              } ${barColors[score]}`}
            ></div>
          </div>

          <p
            className={`text-xs font-semibold ${
              score === 0
                ? "text-red-500"
                : score === 1
                  ? "text-orange-500"
                  : score === 2
                    ? "text-yellow-500"
                    : "text-green-600"
            }`}
          >
            Password Strength: {strengthLabels[score]}
          </p>

          {score < 3 && (
            <p className="text-xs text-gray-600 italic">
              Consider avoiding common words, repeated characters, or sequential
              numbers.
            </p>
          )}
        </div>
      )}
    </>
  );
}
